import { useNavigate } from "react-router-dom";
import { X, ArrowUpRight, Boxes, AlertTriangle, CalendarClock, Warehouse, Sparkles } from "lucide-react";
import { useAssistantOptional } from "@/context/AssistantContext";
import { cn } from "@/lib/utils";
import { fmtDate, relDays } from "@/lib/format";
import { label, MODULE_STATUSES, TONE_DOT, toneOf } from "@/lib/labels";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RigIsoPreview } from "./RigIsoPreview";
import { alertCount } from "./mapUtils";

const Stat = ({ value, unit, hint, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[17px] font-semibold leading-6 tracking-tight tabular">
      {value}
      {unit && <span className="ml-1 text-[12px] font-normal text-muted-foreground">{unit}</span>}
    </div>
    <div className="truncate text-[11.5px] leading-4 text-muted-foreground">{hint}</div>
  </div>
);

const Action = ({ onClick, children, primary, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-[12.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
      primary ? "bg-foreground text-background hover:bg-foreground/90" : "text-foreground/80 hover:bg-muted hover:text-foreground"
    )}
    data-testid={testId}
  >
    {children}
    {!primary && <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />}
  </button>
);

/**
 * Contextual card for a selected map location. `variant="floating"` sits on the map (Oversikt), `variant="panel"`
 * fills a side panel (Kart). Same content, different chrome.
 */
export const MapPreviewCard = ({ loc, onClose, variant = "floating", className, dateLabel }) => {
  const assistant = useAssistantOptional();
  const navigate = useNavigate();
  if (!loc) return null;
  const isProject = loc.kind === "project_site" && loc.project;
  const p = loc.project || {};
  const alerts = alertCount(loc);
  const total = loc.modules_total || 0;
  const statuses = MODULE_STATUSES.filter((s) => (loc.by_status || {})[s] > 0);

  return (
    <div
      className={cn(
        variant === "floating" ? "pointer-events-auto w-[372px] max-w-[calc(100%-1.5rem)] rounded-xl bg-card/95 shadow-pop backdrop-blur-md fade-up" : "flex h-full flex-col",
        className
      )}
      data-testid="map-preview-card"
    >
      <div className={cn("flex items-start gap-3", variant === "floating" ? "p-4 pb-3" : "px-5 pt-5 pb-3")}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            {isProject ? (
              <>
                <span className="font-mono">{p.project_number}</span>
                {p.customer_name && <span className="truncate">· {p.customer_name}</span>}
              </>
            ) : (
              <>
                <Warehouse className="h-3 w-3" strokeWidth={1.75} /> {loc.is_main ? "Hoveddepot" : "Depot"}
                {loc.municipality && <span>· {loc.municipality}</span>}
              </>
            )}
          </div>
          <h3 className="mt-0.5 line-clamp-2 text-[15px] font-semibold leading-5 tracking-tight" data-testid="map-preview-title">
            {isProject ? p.name : loc.name}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {isProject && <StatusBadge status={p.status} entity="project" />}
            {dateLabel && <span className="text-[11.5px] text-muted-foreground">{dateLabel}</span>}
            {alerts > 0 && (
              <span className="inline-flex items-center gap-1 text-[12px] text-amber-700 dark:text-amber-300" data-testid="map-preview-alerts">
                <AlertTriangle className="h-3 w-3" /> {alerts} krever handling
              </span>
            )}
          </div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Lukk" className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground" data-testid="map-preview-close">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className={cn("space-y-4", variant === "floating" ? "px-4" : "flex-1 overflow-y-auto px-5 scrollbar-thin")}>
        {isProject && loc.rig_preview && (
          <div className="rounded-lg bg-muted/50 px-3 py-2" data-testid="map-preview-rig">
            <RigIsoPreview preview={loc.rig_preview} height={variant === "floating" ? 92 : 140} />
            <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Riggdesign · {loc.rig_preview.boxes.length} moduler · {loc.rig_preview.floors} etasje{loc.rig_preview.floors > 1 ? "r" : ""}</span>
              <button type="button" className="link" onClick={() => navigate(`/prosjekter/${p.id}/riggdesign`)} data-testid="map-preview-rig-link">
                Åpne 3D
              </button>
            </div>
          </div>
        )}

        {isProject ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Stat value={p.on_site} unit={p.planned_modules ? `/ ${p.planned_modules}` : undefined} hint="moduler på tomt" testId="map-preview-modules" />
              <Stat value={loc.beds || 0} hint="senger på tomt" testId="map-preview-beds" />
              <Stat value={p.capacity_persons || "–"} hint="personer" testId="map-preview-capacity" />
            </div>
            <div>
              <div className="flex items-center justify-between text-[11.5px] text-muted-foreground">
                <span>Fremdrift</span>
                <span className="tabular">{p.progress_pct} %</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid="map-preview-progress">
                <div className="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${p.progress_pct}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-muted-foreground tabular">
                <span>{fmtDate(p.start_date, "d. MMM yyyy")}</span>
                <span>{fmtDate(p.end_date, "d. MMM yyyy")}</span>
              </div>
            </div>
            {p.next_event ? (
              <div className="flex items-start gap-2.5 rounded-lg bg-muted/50 px-3 py-2" data-testid="map-preview-next">
                <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <div className="min-w-0 leading-tight">
                  <div className="text-[11.5px] text-muted-foreground">
                    Neste · <span className="first-letter:uppercase">{relDays(p.next_event.date)}</span> · {fmtDate(p.next_event.date, "EEE d. MMM")}
                    {p.next_event.time_window && <span> · {p.next_event.time_window}</span>}
                  </div>
                  <div className="truncate text-[12.5px]">
                    {p.next_event.title}
                    {p.next_event.ref && <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{p.next_event.ref}</span>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-[12px] text-muted-foreground">Ingen planlagte hendelser de neste 30 dagene.</div>
            )}
            {alerts > 0 && (
              <ul className="space-y-1 text-[12.5px]" data-testid="map-preview-alert-list">
                {loc.alerts.issues_open > 0 && (
                  <li className="flex items-center gap-2">
                    <span className={cn("h-1.5 w-1.5 rounded-full", loc.alerts.issues_critical ? TONE_DOT.danger : TONE_DOT.warning)} /> {loc.alerts.issues_open} åpne avvik{loc.alerts.issues_critical ? ` (${loc.alerts.issues_critical} høy/kritisk)` : ""}
                  </li>
                )}
                {loc.alerts.service_overdue > 0 && (
                  <li className="flex items-center gap-2">
                    <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT.warning)} /> {loc.alerts.service_overdue} service forfalt
                  </li>
                )}
                {loc.alerts.conflicts > 0 && (
                  <li className="flex items-center gap-2">
                    <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT.danger)} /> {loc.alerts.conflicts} modulkonflikter i planlegging
                  </li>
                )}
              </ul>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Stat value={total} hint="moduler på depot" testId="map-preview-modules" />
              <Stat value={(loc.by_status || {}).ledig || 0} hint="ledige nå" testId="map-preview-available" />
              <Stat value={loc.capacity_modules ? `${Math.round((total / loc.capacity_modules) * 100)} %` : "–"} hint={loc.capacity_modules ? `av ${loc.capacity_modules} plasser` : "kapasitet ukjent"} testId="map-preview-capacity" />
            </div>
            {total > 0 && (
              <div>
                <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid="map-preview-status-bar">
                  {statuses.map((s) => (
                    <div key={s} className={cn("h-full", TONE_DOT[toneOf(s)])} style={{ width: `${(loc.by_status[s] / total) * 100}%` }} title={`${label(s)}: ${loc.by_status[s]}`} />
                  ))}
                </div>
                <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[12px]">
                  {statuses.map((s) => (
                    <li key={s} className="flex items-center gap-2">
                      <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT[toneOf(s)])} aria-hidden />
                      <span className="flex-1 truncate">{label(s)}</span>
                      <span className="tabular text-muted-foreground">{loc.by_status[s]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {loc.address && <div className="text-[12px] text-muted-foreground">{loc.address}</div>}
          </>
        )}
      </div>

      <div className={cn("flex flex-wrap items-center gap-1", variant === "floating" ? "p-3 pt-3" : "px-4 py-4")}>
        {isProject ? (
          <>
            <Action primary onClick={() => navigate(`/prosjekter/${p.id}`)} testId="map-preview-open-project">
              Åpne prosjekt
            </Action>
            <Action onClick={() => navigate(`/planlegging?project=${p.id}`)} testId="map-preview-open-planning">
              Planlegging
            </Action>
            <Action onClick={() => navigate(`/moduler?view=kart&location=${loc.id}`)} testId="map-preview-open-fleet">
              Moduler
            </Action>
            {assistant && (
              <Action onClick={() => assistant.ask(`Gi meg status på ${p.name} – moduler, transporter og åpne avvik`, { project_id: p.id, location_id: loc.id })} testId="map-preview-ask-ai">
                <Sparkles className="h-3.5 w-3.5" /> Spør assistenten
              </Action>
            )}
          </>
        ) : (
          <>
            <Action primary onClick={() => navigate(`/moduler?view=kart&location=${loc.id}`)} testId="map-preview-open-fleet">
              <Boxes className="h-3.5 w-3.5" /> Vis moduler
            </Action>
            <Action onClick={() => navigate(`/moduler?status=ledig`)} testId="map-preview-open-available">
              Ledige
            </Action>
            {assistant && (
              <Action onClick={() => assistant.ask(`Hva står på ${loc.name} nå, og hvor mye ledig plass er det?`, { location_id: loc.id })} testId="map-preview-ask-ai">
                <Sparkles className="h-3.5 w-3.5" /> Spør assistenten
              </Action>
            )}
          </>
        )}
      </div>
    </div>
  );
};
